import React, { useEffect, useState } from 'react';
import { FoodEntry, fetchDayDetail } from '../../hooks/useNutritionSummary';

/**
 * NUTRITION DAY VIEW - Phase 9c
 *
 * Full day detail view matching FatSecret's Daily_Detail layout.
 * Shows meals with food items in a table format.
 */

interface NutritionDayViewProps {
  clientId: string;
  date: string;
  onBack: () => void;
}

// Nutrition columns matching FatSecret screenshot with dividers
// divider: 'thick' after this column, 'thin' = normal, 'none' = no divider
const nutritionColumns = [
  { key: 'calories', label: 'Cals', divider: 'thick' },
  // Fats group
  { key: 'fats_g', label: 'Fat', divider: 'thin' },
  { key: 'saturated_fat_g', label: 'Sat', divider: 'thin' },
  { key: 'polyunsaturated_fat_g', label: 'Poly', divider: 'thin' },
  { key: 'monounsaturated_fat_g', label: 'Mono', divider: 'thick' },
  // Carbs group
  { key: 'carbs_g', label: 'Carbs', divider: 'thin' },
  { key: 'fiber_g', label: 'Fiber', divider: 'thin' },
  { key: 'sugar_g', label: 'Sugar', divider: 'thick' },
  // Protein
  { key: 'protein_g', label: 'Prot', divider: 'thick' },
  // Minerals
  { key: 'cholesterol_mg', label: 'Chol', divider: 'thin' },
  { key: 'sodium_mg', label: 'Sod', divider: 'thin' },
  { key: 'potassium_mg', label: 'Potas', divider: 'none' },
];

// Group entries by meal type (merging snacks and other)
const groupByMeal = (entries: FoodEntry[]): Record<string, FoodEntry[]> => {
  const groups: Record<string, FoodEntry[]> = {
    breakfast: [],
    lunch: [],
    dinner: [],
    'snacks/other': [],
  };

  entries.forEach(entry => {
    const mealType = entry.meal_type || 'other';
    if (mealType === 'snacks' || mealType === 'other') {
      groups['snacks/other'].push(entry);
    } else if (groups[mealType]) {
      groups[mealType].push(entry);
    } else {
      groups['snacks/other'].push(entry);
    }
  });

  return groups;
};

// Calculate totals for entries
const calculateTotals = (entries: FoodEntry[]) => {
  const totals: Record<string, number> = {};
  nutritionColumns.forEach(col => {
    totals[col.key] = entries.reduce((sum, e) => sum + ((e as any)[col.key] || 0), 0);
  });
  return totals;
};

// Format number - show dash for zero/undefined, round to 2 decimals max
const formatValue = (value: number | undefined): string => {
  if (value === undefined || value === null || value === 0) return '-';
  // Round to 2 decimal places, but show whole number if no decimals needed
  const rounded = Math.round(value * 100) / 100;
  return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(2);
};

// Format date for header
const formatDateHeader = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  });
};

// Meal order for display
const mealOrder = ['breakfast', 'lunch', 'dinner', 'snacks/other'];
const mealLabels: Record<string, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  'snacks/other': 'Snacks/Other',
};

export const NutritionDayView: React.FC<NutritionDayViewProps> = ({
  clientId,
  date,
  onBack: _onBack,
}) => {
  const [entries, setEntries] = useState<FoodEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      const result = await fetchDayDetail(clientId, date);
      setEntries(result.entries);
      setError(result.error);
      setLoading(false);
    };
    loadData();
  }, [clientId, date]);

  if (loading) {
    return (
      <div className="day-view loading">
        <div className="loading-spinner" />
        <p>Loading meals...</p>
        <style>{styles}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="day-view error">
        <p>Error: {error}</p>
        <style>{styles}</style>
      </div>
    );
  }

  const groupedEntries = groupByMeal(entries);
  const dayTotals = calculateTotals(entries);

  return (
    <div className="day-view">
      {/* Header */}
      <div className="day-header">
        <h3>{formatDateHeader(date)}</h3>
      </div>

      {entries.length === 0 ? (
        <div className="no-data">
          <p>No food entries for this day</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="foods-table">
            <thead>
              <tr>
                <th className="col-food divider-thick">FOODS EATEN</th>
                {nutritionColumns.map(col => (
                  <th key={col.key} className={`col-nutrient divider-${col.divider}`}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mealOrder.map(mealKey => {
                const mealEntries = groupedEntries[mealKey];
                if (mealEntries.length === 0) return null;

                const mealTotals = calculateTotals(mealEntries);

                return (
                  <React.Fragment key={mealKey}>
                    {/* Meal Header Row with Totals */}
                    <tr className="meal-header-row">
                      <td className="col-food divider-thick">
                        <span className="meal-label">{mealLabels[mealKey]}</span>
                      </td>
                      {nutritionColumns.map(col => (
                        <td key={col.key} className={`col-nutrient meal-total divider-${col.divider}`}>
                          {formatValue(mealTotals[col.key])}
                        </td>
                      ))}
                    </tr>
                    {/* Food Items */}
                    {mealEntries.map(entry => (
                      <tr key={entry.id} className="food-row">
                        <td className="col-food divider-thick">
                          <div className="food-name">{entry.food_name}</div>
                          {entry.serving_size && (
                            <div className="serving-size">{entry.serving_size}</div>
                          )}
                        </td>
                        {nutritionColumns.map(col => (
                          <td key={col.key} className={`col-nutrient divider-${col.divider}`}>
                            {formatValue((entry as any)[col.key])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td className="col-food divider-thick">
                  <span className="total-label">Total</span>
                </td>
                {nutritionColumns.map(col => (
                  <td key={col.key} className={`col-nutrient divider-${col.divider}`}>
                    {formatValue(dayTotals[col.key])}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .day-view {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .day-view.loading,
  .day-view.error {
    align-items: center;
    justify-content: center;
    color: var(--text-secondary);
  }

  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--border-secondary);
    border-top-color: var(--accent-green);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Header */
  .day-header {
    padding: 12px 0;
    border-bottom: 1px solid var(--border-secondary);
    margin-bottom: 16px;
  }

  .day-header h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
  }

  /* No Data */
  .no-data {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    color: var(--text-secondary);
  }

  /* Table */
  .table-wrapper {
    flex: 1;
    overflow: auto;
    background: var(--bg-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: var(--radius-medium);
  }

  .foods-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  /* Header */
  .foods-table thead {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .foods-table th {
    background: var(--bg-tertiary);
    border-bottom: 2px solid var(--border-secondary);
    padding: 8px 10px;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: var(--text-secondary);
  }

  .foods-table th.col-food {
    text-align: left;
    min-width: 200px;
  }

  .foods-table th.col-nutrient {
    text-align: center;
    min-width: 50px;
    white-space: nowrap;
  }

  /* Meal Header Row */
  .meal-header-row {
    background: var(--bg-tertiary);
  }

  .meal-header-row td {
    padding: 8px 10px;
    border-bottom: 1px solid var(--border-secondary);
    font-weight: 600;
  }

  .meal-label {
    color: var(--text-primary);
    font-size: 13px;
  }

  .meal-total {
    color: var(--text-primary);
    text-align: right;
  }

  /* Food Row */
  .food-row td {
    padding: 6px 10px;
    border-bottom: 1px solid var(--border-secondary);
    vertical-align: top;
  }

  .food-row:hover {
    background: rgba(var(--accent-green-rgb, 16, 185, 129), 0.05);
  }

  .food-name {
    color: var(--text-primary);
    font-size: 13px;
  }

  .serving-size {
    color: var(--text-tertiary);
    font-size: 11px;
    font-style: italic;
    margin-top: 2px;
  }

  .col-nutrient {
    text-align: right;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
  }

  /* Column Dividers */
  .divider-thin {
    border-right: 1px solid var(--border-secondary);
  }

  .divider-thick {
    border-right: 2px solid var(--text-tertiary);
  }

  .divider-none {
    border-right: none;
  }

  /* Total Row */
  .foods-table tfoot {
    position: sticky;
    bottom: 0;
  }

  .total-row td {
    background: var(--bg-tertiary);
    border-top: 2px solid var(--border-secondary);
    padding: 10px;
    font-weight: 600;
  }

  .total-label {
    color: var(--text-primary);
    font-size: 13px;
  }

  .total-row .col-nutrient {
    color: var(--accent-green);
  }
`;

export default NutritionDayView;
